import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check } from 'lucide-react';
import { MarkdownContent } from '@/components/ui';
import { useAuth } from '../../store/auth-context';
import { routes } from '../../lib/routes';
import type { ActivityItem, ActivitySource, ActivityTriggerSource, ActivityPriority } from '../../types';
import { formatRelativeTime } from './utils';
import { cleanDisplayText } from '@/utils/display-text';

const SOURCE_COLORS: Record<ActivitySource, string> = {
  agent: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  email: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  scheduler: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  system: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const TRIGGER_SOURCE_LABELS: Record<ActivityTriggerSource, string> = {
  user: 'User',
  email: 'Email',
  scheduler: 'Scheduler',
  webhook: 'Webhook',
  api: 'API',
  event: 'Event',
};

const TRIGGER_SOURCE_COLORS: Record<ActivityTriggerSource, string> = {
  user: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  email: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  scheduler: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  webhook: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  api: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  event: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

interface ActivityCardProps {
  activity: ActivityItem;
  isRealtime?: boolean;
}

/**
 * Build the entity-link table scoped to a target spaceId. Activity rows
 * surface from cross-space `/me/activities`, but every console-side link
 * needs a space — we use the activity's own spaceId when present, falling
 * back to the user's default (Personal) space.
 */
function buildEntityLinks(spaceId: string): { key: string; label: string; route: (id: string) => string }[] {
  return [
    { key: 'agentId', label: 'Agent', route: (id) => routes.console.spaceAgent(spaceId, id) },
    { key: 'skillId', label: 'Skill', route: (id) => routes.console.spaceSkill(spaceId, id) },
    { key: 'toolId', label: 'Tool', route: (id) => routes.console.spaceTool(spaceId, id) },
    { key: 'integrationId', label: 'Integration', route: (id) => routes.console.spaceIntegration(spaceId, id) },
    { key: 'scheduleId', label: 'Schedule', route: () => routes.console.spaceAgentSchedules(spaceId) },
    { key: 'conversationId', label: 'Conversation', route: (id) => routes.operator.chat(id) },
    { key: 'emailId', label: 'Email', route: (id) => routes.console.spaceConnectionEmail(spaceId, id) },
  ];
}

export default function ActivityCard({ activity, isRealtime }: ActivityCardProps) {
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  // `spaceId` is always present on items from `/me/activities` post-migration,
  // but keep the defaultSpaceId fallback as defensive code for stale rows.
  const targetSpaceId = activity.spaceId || user?.defaultSpaceId || '';
  const entityLinks = useMemo(() => buildEntityLinks(targetSpaceId), [targetSpaceId]);

  function handleCopy() {
    navigator.clipboard.writeText(JSON.stringify(activity, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  const related = activity.relatedEntityIds;

  return (
    <div className="group relative rounded-lg border bg-card p-4">
      {/* Copy button — visible on hover */}
      <button
        type="button"
        onClick={handleCopy}
        title="Copy activity JSON"
        className="absolute top-2 right-2 p-1 rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
      </button>

      {/* Top row: dot + timestamp + badges */}
      <div className="flex items-center gap-2 mb-1.5">
        {isRealtime && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {formatRelativeTime(activity.createdAt)}
        </span>
        <Badge className={SOURCE_COLORS[activity.source] ?? SOURCE_COLORS.system}>
          {activity.source}
        </Badge>
        {activity.triggerSource && (
          <Badge className={TRIGGER_SOURCE_COLORS[activity.triggerSource] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'}>
            {TRIGGER_SOURCE_LABELS[activity.triggerSource] ?? activity.triggerSource}
          </Badge>
        )}
        {activity.category && (
          <Badge className="bg-muted text-muted-foreground">
            {activity.category}
          </Badge>
        )}
        {(['high', 'urgent'] as ActivityPriority[]).includes(activity.priority) && (
          <Badge className={PRIORITY_COLORS[activity.priority]}>
            {activity.priority}
          </Badge>
        )}
      </div>

      {/* Title */}
      <p className="text-sm font-medium">{cleanDisplayText(activity.title)}</p>

      {/* Summary */}
      {activity.summary && (
        <MarkdownContent className="text-xs text-muted-foreground mt-0.5">{activity.summary}</MarkdownContent>
      )}

      {/* Related entities */}
      {related && (
        <div className="flex items-center gap-1.5 mt-2">
          {entityLinks.map(({ key, label, route }) => {
            const id = related[key as keyof typeof related];
            if (!id) return null;
            return (
              <Link
                key={key}
                to={route(id)}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors"
              >
                {label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${className}`}>
      {children}
    </span>
  );
}
